import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { BaseFilterButton } from 'components/base-table/filters';
import { usePageParams } from 'hooks/use-page-params';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  brandId?: {
    value: number;
    label: string;
  };
};

type TBrandOption = {
  value: number;
  label: string;
};

const defaultValues: TInputs = {
  brandId: undefined,
};

export const BrandFilterButton: React.FC = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const [selectedBrand, setSelectedBrand] = useState<TBrandOption | null>(null);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  // Update active filter button
  useEffect(() => {
    if (params.brandId) setIsActive.on();
    else setIsActive.off();
  }, [params.brandId, setIsActive]);

  // Dynamic search function that calls the API with search term
  const loadBrandOptions = useCallback(async (inputValue: string) => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: inputValue ? 50 : 20, // More results when searching, fewer for default
          ...(inputValue && { name: inputValue }), // Only add name param if there's input
        },
      });

      return (data?.data || []).map((brand: any) => ({
        value: brand.id,
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  }, []);

  const onSubmit = (data: TInputs) => {
    if (data.brandId?.value) {
      params.brandId = data.brandId.value;
    } else {
      delete params.brandId;
    }
    params.page = 1; // Reset to first page when filtering
    setPageParams(params);
    onClose();
    reset(data);
  };

  const onClear = () => {
    const newParams = { ...params };
    delete newParams.brandId;
    newParams.page = 1;
    setPageParams(newParams);
    reset();
    setSelectedBrand(null);
    onClose();
  };

  // Load current brand details when filter opens
  useEffect(() => {
    if (isOpen && params.brandId) {
      // Fetch the specific brand to get its name
      const fetchCurrentBrand = async () => {
        try {
          const { data } = await http.get('ums/v1/crm/management/brands', {
            params: {
              page: 1,
              limit: 1000, // Large limit to find the brand
            },
          });

          const brand = data?.data?.find((b: any) => b.id === Number(params.brandId));
          if (brand) {
            const brandOption = {
              value: brand.id,
              label: brand.name,
            };
            setValue('brandId', brandOption);
            setSelectedBrand(brandOption);
          }
        } catch (error) {
          console.error('Error loading current brand:', error);
          // Fallback
          const fallbackOption = {
            value: Number(params.brandId),
            label: `Brand ${params.brandId}`,
          };
          setValue('brandId', fallbackOption);
          setSelectedBrand(fallbackOption);
        }
      };

      fetchCurrentBrand();
    } else if (isOpen && !params.brandId) {
      reset();
      setSelectedBrand(null);
    }
  }, [isOpen, params.brandId, setValue, reset]);

  const handleClose = () => {
    onClose();
  };

  // Custom onChange handler to prevent auto-close
  const handleBrandChange = useCallback(
    (selectedOption: any) => {
      setSelectedBrand(selectedOption);
      setValue('brandId', selectedOption);
    },
    [setValue]
  );

  // Prevent form submission from closing the modal
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleFormSubmit} onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <FormLabel>Brand</FormLabel>
            <Controller
              name='brandId'
              control={control}
              render={({ field }) => (
                <ChakraAsyncSelect
                  {...field}
                  value={selectedBrand}
                  loadOptions={loadBrandOptions}
                  defaultOptions={true} // Load default options on mount
                  placeholder='Search and select a brand...'
                  isClearable
                  cacheOptions={false} // Disable cache to allow fresh searches
                  loadingMessage={() => 'Searching brands...'}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? `No brands found for "${inputValue}"` : 'Type to search brands'
                  }
                  // Critical: Prevent dropdown from auto-closing
                  closeMenuOnSelect={false}
                  blurInputOnSelect={false}
                  // Custom change handler
                  onChange={handleBrandChange}
                  // Additional properties to prevent auto-close
                  menuShouldBlockScroll={false}
                  menuShouldScrollIntoView={false}
                  // Ensure menu stays open
                  menuIsOpen={undefined} // Let the component control this
                />
              )}
            />
          </FormControl>

          <Stack direction='row' justify='flex-end' spacing={2}>
            <Button variant='outline' colorScheme='red' onClick={onClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='gray' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='blue' type='submit' isLoading={isSubmitting}>
              Filter
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};
